import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Wrench, AlertTriangle, Bell, Plus, Loader2, X, Calendar,
    Truck, DollarSign, CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Toast } from "./master/assets";
import EmptyState from "../components/EmptyState";
import dict from "../i18n/es-MX";

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
import { fmtDate } from "../lib/dateFormat";

const TABS = ["logs", "reminders", "alerts"];
const TAB_PARAM_MAP = { logs: "logs", reminders: "recordatorios", alerts: "alertas" };
const REVERSE_TAB_PARAM = { logs: "logs", recordatorios: "reminders", alertas: "alerts" };

function LogForm({ assets, reminders, onSaved, onCancel, prefill, initial }) {
    const [form, setForm] = useState({
        asset_id: initial?.asset_id || prefill?.asset_id || "",
        date: initial?.date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        kind: initial?.kind || "preventivo",
        description: initial?.description || "",
        parts_cost_mxn: initial?.parts_cost_mxn ?? "",
        labor_cost_mxn: initial?.labor_cost_mxn ?? "",
        vendor: initial?.vendor || "",
        odometer_km: initial?.meter_at_service?.odometer_km ?? "",
        hour_meter_horas: initial?.meter_at_service?.hour_meter_horas ?? "",
        notes: initial?.notes || "",
        reminder_id: initial?.scheduled_from_reminder_id || prefill?.reminder_id || "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const assetReminders = useMemo(
        () => reminders.filter(r => r.asset_id === form.asset_id && r.is_active),
        [reminders, form.asset_id],
    );
    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const submit = async () => {
        if (!form.asset_id || !form.description.trim()) { setErr(dict.maintenance.requiredAssetDesc); return; }
        setBusy(true); setErr("");
        const payload = {
            asset_id: form.asset_id,
            date: form.date,
            kind: form.kind,
            description: form.description.trim(),
            parts_cost_mxn: parseFloat(form.parts_cost_mxn || 0),
            labor_cost_mxn: parseFloat(form.labor_cost_mxn || 0),
            vendor: form.vendor.trim() || null,
            odometer_km: form.odometer_km ? parseFloat(form.odometer_km) : null,
            hour_meter_horas: form.hour_meter_horas ? parseFloat(form.hour_meter_horas) : null,
            notes: form.notes.trim() || null,
            reminder_id: form.reminder_id || null,
        };
        try {
            if (initial?.id) {
                await api.patch(`/maintenance/logs/${initial.id}`, payload);
            } else {
                await api.post("/maintenance/logs", payload);
            }
            onSaved(initial?.id ? "edited" : "created");
        }
        catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="log-form-modal">
            <form noValidate onClick={e => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Wrench size={16} className="text-brand-orange"/>{initial ? dict.maintenance.editLog : dict.maintenance.newLog}</h2>
                    <button type="button" onClick={onCancel} className="w-8 h-8 rounded hover:bg-slate-800 inline-flex items-center justify-center"><X size={16}/></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.asset} *</span>
                        <select value={form.asset_id} onChange={e => update("asset_id", e.target.value)} data-testid="log-asset"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                            <option value="">—</option>
                            {assets.map(a => <option key={a.id} value={a.id}>{a.nickname}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.date}</span>
                        <input type="date" value={form.date} onChange={e => update("date", e.target.value)} data-testid="log-date"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.kind}</span>
                        <select value={form.kind} onChange={e => update("kind", e.target.value)} data-testid="log-kind"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                            <option value="preventivo">{dict.maintenance.preventivo}</option>
                            <option value="correctivo">{dict.maintenance.correctivo}</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.vendor}</span>
                        <input type="text" value={form.vendor} onChange={e => update("vendor", e.target.value)} data-testid="log-vendor"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                </div>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.description} *</span>
                    <textarea value={form.description} onChange={e => update("description", e.target.value)} data-testid="log-description"
                        className="w-full mt-1 min-h-[70px] px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.parts}</span>
                        <input type="number" step="0.01" min="0" value={form.parts_cost_mxn} onChange={e => update("parts_cost_mxn", e.target.value)} data-testid="log-parts"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.labor}</span>
                        <input type="number" step="0.01" min="0" value={form.labor_cost_mxn} onChange={e => update("labor_cost_mxn", e.target.value)} data-testid="log-labor"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.odometerKm}</span>
                        <input type="number" step="0.1" min="0" value={form.odometer_km} onChange={e => update("odometer_km", e.target.value)} data-testid="log-odometer"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.hoursMeter}</span>
                        <input type="number" step="0.1" min="0" value={form.hour_meter_horas} onChange={e => update("hour_meter_horas", e.target.value)} data-testid="log-hours"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                </div>
                {assetReminders.length > 0 && (
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.linkReminder}</span>
                        <select value={form.reminder_id} onChange={e => update("reminder_id", e.target.value)} data-testid="log-reminder"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                            <option value="">—</option>
                            {assetReminders.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                    </label>
                )}
                {err && <div className="text-xs text-red-300" data-testid="log-form-err">{err}</div>}
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onCancel} className="flex-1 h-10 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm">{dict.common.cancel}</button>
                    <button type="submit" disabled={busy} data-testid="log-save"
                        className="flex-1 h-10 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                        {busy ? <Loader2 className="animate-spin" size={13}/> : <Wrench size={13}/>} {dict.common.save}
                    </button>
                </div>
            </form>
        </div>
    );
}

function ReminderForm({ assets, onSaved, onCancel }) {
    const [form, setForm] = useState({
        asset_id: "", label: "", interval_km: "", interval_horas: "", interval_days: "",
        last_serviced_odometer_km: "", last_serviced_hour_meter_horas: "",
        last_serviced_date: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const submit = async () => {
        if (!form.asset_id || !form.label.trim()) { setErr(dict.maintenance.requiredAssetLabel); return; }
        if (!form.interval_km && !form.interval_horas && !form.interval_days) {
            setErr(dict.maintenance.requiredInterval); return;
        }
        setBusy(true); setErr("");
        const payload = {
            asset_id: form.asset_id, label: form.label.trim(),
            interval_km: form.interval_km ? parseFloat(form.interval_km) : null,
            interval_horas: form.interval_horas ? parseFloat(form.interval_horas) : null,
            interval_days: form.interval_days ? parseInt(form.interval_days) : null,
            last_serviced_odometer_km: form.last_serviced_odometer_km ? parseFloat(form.last_serviced_odometer_km) : null,
            last_serviced_hour_meter_horas: form.last_serviced_hour_meter_horas ? parseFloat(form.last_serviced_hour_meter_horas) : null,
            last_serviced_date: form.last_serviced_date || null,
        };
        try { await api.post("/maintenance/reminders", payload); onSaved(); }
        catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="reminder-form-modal">
            <form noValidate onClick={e => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Bell size={16} className="text-brand-orange"/>{dict.maintenance.newReminder}</h2>
                    <button type="button" onClick={onCancel} className="w-8 h-8 rounded hover:bg-slate-800 inline-flex items-center justify-center"><X size={16}/></button>
                </div>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.asset} *</span>
                    <select value={form.asset_id} onChange={e => update("asset_id", e.target.value)} data-testid="rem-asset"
                        className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                        <option value="">—</option>
                        {assets.map(a => <option key={a.id} value={a.id}>{a.nickname}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.label} *</span>
                    <input type="text" value={form.label} onChange={e => update("label", e.target.value)} data-testid="rem-label"
                        className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </label>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.intervalsHelp}</div>
                <div className="grid grid-cols-3 gap-3">
                    <label className="block">
                        <span className="text-[10px] text-slate-500">{dict.maintenance.intervalKm}</span>
                        <input type="number" step="0.1" min="0" value={form.interval_km} onChange={e => update("interval_km", e.target.value)} data-testid="rem-km"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                    <label className="block">
                        <span className="text-[10px] text-slate-500">{dict.maintenance.intervalHours}</span>
                        <input type="number" step="0.1" min="0" value={form.interval_horas} onChange={e => update("interval_horas", e.target.value)} data-testid="rem-hours"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                    <label className="block">
                        <span className="text-[10px] text-slate-500">{dict.maintenance.intervalDays}</span>
                        <input type="number" step="1" min="0" value={form.interval_days} onChange={e => update("interval_days", e.target.value)} data-testid="rem-days"
                            className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    </label>
                </div>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{dict.maintenance.lastServicedDate}</span>
                    <input type="date" value={form.last_serviced_date} onChange={e => update("last_serviced_date", e.target.value)} data-testid="rem-last-date"
                        className="w-full mt-1 h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </label>
                {err && <div className="text-xs text-red-300" data-testid="rem-form-err">{err}</div>}
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onCancel} className="flex-1 h-10 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm">{dict.common.cancel}</button>
                    <button type="submit" disabled={busy} data-testid="rem-save"
                        className="flex-1 h-10 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                        {busy ? <Loader2 className="animate-spin" size={13}/> : <Bell size={13}/>} {dict.common.save}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function Maintenance() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = REVERSE_TAB_PARAM[searchParams.get("tab")] || "logs";
    const initialFilter = searchParams.get("filter") || ""; // "overdue" | "due_soon" | ""
    const [tab, setTabState] = useState(initialTab);
    const [alertFilter, setAlertFilter] = useState(initialFilter);
    const setTab = (next) => {
        setTabState(next);
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", TAB_PARAM_MAP[next] || next);
        if (next !== "alerts") sp.delete("filter");
        setSearchParams(sp, { replace: true });
    };
    const [logs, setLogs] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [alerts, setAlerts] = useState({ overdue: [], due_soon: [], total_overdue_count: 0, total_due_soon_count: 0 });
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logFormOpen, setLogFormOpen] = useState(false);
    const [editingLog, setEditingLog] = useState(null);
    const [logPrefill, setLogPrefill] = useState({});
    const [remFormOpen, setRemFormOpen] = useState(false);
    const [toast, setToast] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const [a, l, r, al] = await Promise.all([
                api.get("/assets"),
                api.get("/maintenance/logs"),
                api.get("/maintenance/reminders"),
                api.get("/maintenance/alerts"),
            ]);
            setAssets(a.data || []);
            setLogs(l.data || []);
            setReminders(r.data || []);
            setAlerts(al.data || alerts);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const assetById = useMemo(() => Object.fromEntries(assets.map(a => [a.id, a])), [assets]);
    const intervalText = (r) => {
        const out = [];
        if (r.interval_km) out.push(`${r.interval_km.toLocaleString("es-MX")} km`);
        if (r.interval_horas) out.push(`${r.interval_horas} h`);
        if (r.interval_days) out.push(`${r.interval_days} ${dict.maintenance.days}`);
        return out.join(" / ") || "—";
    };
    const nextDueText = (r) => {
        const out = [];
        if (r.next_due_meter?.odometer_km) out.push(`${r.next_due_meter.odometer_km.toLocaleString("es-MX")} km`);
        if (r.next_due_meter?.hour_meter_horas) out.push(`${r.next_due_meter.hour_meter_horas} h`);
        if (r.next_due_date) out.push(fmtDate(r.next_due_date));
        return out.join(" • ") || "—";
    };
    const statusBadge = (st) => {
        const m = {
            overdue:  ["bg-red-500/15 text-red-300 border-red-500/40",       dict.maintenance.overdue],
            due_soon: ["bg-amber-500/15 text-amber-300 border-amber-500/40", dict.maintenance.dueSoon],
            ok:       ["bg-emerald-500/15 text-emerald-300 border-emerald-500/40", dict.maintenance.ok],
        };
        const [cls, label] = m[st] || m.ok;
        return <span className={`px-2 h-5 text-[10px] rounded-full border inline-flex items-center uppercase tracking-wider ${cls}`}>{label}</span>;
    };

    return (
        <div className="space-y-5" data-testid="maintenance-page">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight inline-flex items-center gap-2">
                        <Wrench className="text-brand-orange" size={26}/> {dict.maintenance.title}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">{dict.maintenance.subtitle}</p>
                </div>
                {tab === "logs" && (
                    <button type="button" onClick={() => { setLogPrefill({}); setLogFormOpen(true); }} data-testid="new-log-btn"
                        className="inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 text-sm font-semibold">
                        <Plus size={14}/> {dict.maintenance.newLog}
                    </button>
                )}
                {tab === "reminders" && (
                    <button type="button" onClick={() => setRemFormOpen(true)} data-testid="new-reminder-btn"
                        className="inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 text-sm font-semibold">
                        <Plus size={14}/> {dict.maintenance.newReminder}
                    </button>
                )}
            </header>

            <div className="flex gap-1 border-b border-slate-800">
                {TABS.map(k => (
                    <button key={k} type="button" onClick={() => setTab(k)} data-testid={`maint-tab-${k}`}
                        className={`-mb-px px-4 h-9 text-xs font-semibold uppercase tracking-wider border-b-2 transition inline-flex items-center gap-1.5 ${tab === k ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                        {k === "logs" && <Wrench size={12}/>}{k === "reminders" && <Bell size={12}/>}{k === "alerts" && <AlertTriangle size={12}/>}
                        {dict.maintenance.tabs[k]}
                        {k === "alerts" && (alerts.total_overdue_count + alerts.total_due_soon_count > 0) && (
                            <span className="ml-1 px-1.5 h-4 text-[10px] rounded-full bg-red-500/20 text-red-300 inline-flex items-center" data-testid="alert-count-badge">
                                {alerts.total_overdue_count + alerts.total_due_soon_count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{err}</div>}
            {loading && <div className="text-center py-10"><Loader2 className="animate-spin inline text-slate-500"/></div>}

            {!loading && tab === "logs" && (
                <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-x-auto" data-testid="logs-tab-content">
                    {logs.length === 0 ? <EmptyState icon={Wrench} title={dict.maintenance.emptyLogs} testid="logs-empty"/> :
                    <table className="min-w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left py-2 px-3">{dict.maintenance.date}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.asset}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.kind}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.description}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.vendor}</th>
                                <th className="text-right py-2 px-3">{dict.maintenance.totalCost}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.meterAtService}</th>
                                <th className="py-2 px-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(l => (
                                <tr key={l.id} className="border-t border-slate-800" data-testid={`log-row-${l.id}`}>
                                    <td className="py-2 px-3 text-slate-400">{fmtDate(l.date)}</td>
                                    <td className="py-2 px-3">{assetById[l.asset_id]?.nickname || "—"}</td>
                                    <td className="py-2 px-3">
                                        <span className={`px-2 h-5 text-[10px] rounded-full border inline-flex items-center uppercase ${l.kind === "preventivo" ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                                            {dict.maintenance[l.kind]}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-slate-300 max-w-xs truncate">{l.description}</td>
                                    <td className="py-2 px-3 text-slate-400">{l.vendor || "—"}</td>
                                    <td className="py-2 px-3 text-right font-mono text-emerald-300">{fmtMXN(l.total_cost_mxn)}</td>
                                    <td className="py-2 px-3 text-xs text-slate-400">
                                        {l.meter_at_service?.odometer_km ? `${l.meter_at_service.odometer_km.toLocaleString("es-MX")} km` : ""}
                                        {l.meter_at_service?.hour_meter_horas ? `${l.meter_at_service.hour_meter_horas} h` : ""}
                                    </td>
                                    <td className="py-2 px-3 text-right whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => { setEditingLog(l); setLogFormOpen(true); }}
                                            data-testid={`log-edit-${l.id}`}
                                            className="h-8 px-2 rounded border border-slate-700 hover:border-brand-orange hover:text-brand-orange text-xs uppercase tracking-wider"
                                        >
                                            {dict.maintenance.editAction}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    }
                </div>
            )}

            {!loading && tab === "reminders" && (
                <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-x-auto" data-testid="reminders-tab-content">
                    {reminders.length === 0 ? <EmptyState icon={Bell} title={dict.maintenance.emptyReminders} testid="reminders-empty"/> :
                    <table className="min-w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left py-2 px-3">{dict.maintenance.asset}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.label}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.interval}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.lastServiced}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.nextDue}</th>
                                <th className="text-left py-2 px-3">{dict.maintenance.status}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reminders.map(r => (
                                <tr key={r.id} className="border-t border-slate-800" data-testid={`reminder-row-${r.id}`}>
                                    <td className="py-2 px-3">{assetById[r.asset_id]?.nickname || "—"}</td>
                                    <td className="py-2 px-3 text-slate-100 font-medium">{r.label}</td>
                                    <td className="py-2 px-3 text-slate-300">{intervalText(r)}</td>
                                    <td className="py-2 px-3 text-slate-400">{r.last_serviced_date ? fmtDate(r.last_serviced_date) : "—"}</td>
                                    <td className="py-2 px-3 text-slate-300">{nextDueText(r)}</td>
                                    <td className="py-2 px-3">{statusBadge(r.status)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    }
                </div>
            )}

            {!loading && tab === "alerts" && (
                <div className="space-y-5" data-testid="alerts-tab-content">
                    {(alertFilter === "overdue" || alertFilter === "due_soon") && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-500 uppercase tracking-wider">{dict.maintenance.filterActive}:</span>
                            <span className="px-2 h-6 rounded-full border border-brand-orange/40 bg-brand-orange/10 text-brand-orange inline-flex items-center" data-testid="active-filter-badge">
                                {alertFilter === "overdue" ? dict.maintenance.overdue : dict.maintenance.dueSoon}
                            </span>
                            <button type="button" onClick={() => { setAlertFilter(""); const sp = new URLSearchParams(searchParams); sp.delete("filter"); setSearchParams(sp, { replace: true }); }}
                                data-testid="clear-filter-btn"
                                className="text-slate-400 hover:text-slate-100 inline-flex items-center gap-1">
                                <X size={11}/> {dict.common.clear}
                            </button>
                        </div>
                    )}
                    {alerts.total_overdue_count + alerts.total_due_soon_count === 0 && (
                        <EmptyState icon={CheckCircle2} title={dict.maintenance.alertsClear} testid="alerts-empty"/>
                    )}
                    {alerts.overdue.length > 0 && alertFilter !== "due_soon" && (
                        <section data-testid="alerts-overdue-section">
                            <h2 className="text-xs uppercase tracking-wider text-red-300 mb-2 inline-flex items-center gap-1"><AlertTriangle size={12}/> {dict.maintenance.overdueSection} ({alerts.total_overdue_count})</h2>
                            <div className="rounded-xl border border-red-500/40 bg-red-500/5 divide-y divide-red-500/20">
                                {alerts.overdue.map(a => (
                                    <div key={a.id} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`alert-overdue-${a.id}`}>
                                        <div>
                                            <div className="text-sm font-semibold text-slate-100 inline-flex items-center gap-2"><Truck size={13}/>{a.asset_nickname} • {a.label}</div>
                                            <div className="text-xs text-red-200/80 mt-0.5">{nextDueText(a)}</div>
                                        </div>
                                        <button type="button" onClick={() => { setLogPrefill({ asset_id: a.asset_id, reminder_id: a.id }); setLogFormOpen(true); }}
                                            data-testid={`register-service-${a.id}`}
                                            className="h-9 px-3 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
                                            <Wrench size={12}/> {dict.maintenance.registerService}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                    {alerts.due_soon.length > 0 && alertFilter !== "overdue" && (
                        <section data-testid="alerts-due-soon-section">
                            <h2 className="text-xs uppercase tracking-wider text-amber-300 mb-2 inline-flex items-center gap-1"><Calendar size={12}/> {dict.maintenance.dueSoonSection} ({alerts.total_due_soon_count})</h2>
                            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 divide-y divide-amber-500/20">
                                {alerts.due_soon.map(a => (
                                    <div key={a.id} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`alert-due-soon-${a.id}`}>
                                        <div>
                                            <div className="text-sm font-semibold text-slate-100 inline-flex items-center gap-2"><Truck size={13}/>{a.asset_nickname} • {a.label}</div>
                                            <div className="text-xs text-amber-200/80 mt-0.5">{nextDueText(a)}</div>
                                        </div>
                                        <button type="button" onClick={() => { setLogPrefill({ asset_id: a.asset_id, reminder_id: a.id }); setLogFormOpen(true); }}
                                            data-testid={`register-service-${a.id}`}
                                            className="h-9 px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
                                            <Wrench size={12}/> {dict.maintenance.registerService}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}

            {logFormOpen && <LogForm assets={assets} reminders={reminders} prefill={logPrefill} initial={editingLog}
                onCancel={() => { setLogFormOpen(false); setEditingLog(null); }}
                onSaved={async (action) => {
                    setLogFormOpen(false); setEditingLog(null); await load();
                    setToast({ msg: action === "edited" ? dict.maintenance.editedOk : dict.common.saved, type: "success" });
                }}/>}
            {remFormOpen && <ReminderForm assets={assets} onCancel={() => setRemFormOpen(false)} onSaved={async () => { setRemFormOpen(false); await load(); }}/>}
            {toast && <Toast {...toast} onClose={() => setToast(null)}/>}
        </div>
    );
}
